import { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';

const projects = [
  {
    title: 'AI Travel Planner',
    description:
      'A smart itinerary platform that suggests personalized trips based on budget, weather, and interests.',
    stack: 'React, Node.js, OpenAI API'
  },
  {
    title: 'FinTrack Dashboard',
    description:
      'A modern analytics dashboard for tracking spending habits, subscriptions, and investment performance.',
    stack: 'React, D3.js, Firebase'
  },
  {
    title: 'Campus Connect',
    description:
      'A student community app for sharing events, clubs, and resources with a real-time discussion board.',
    stack: 'React, Socket.IO, Express'
  },
  {
    title: 'Pixel Portfolio Studio',
    description:
      'A no-code portfolio builder with drag-and-drop modules and export-ready templates for creators.',
    stack: 'React, Tailwind CSS, Supabase'
  }
];

const socialLinks = [
  { name: 'GitHub', href: 'https://github.com/' },
  { name: 'LinkedIn', href: 'https://www.linkedin.com/' },
  { name: 'Email', href: 'mailto:rishi@example.com' }
];

function App() {
  useEffect(() => {
    AOS.init({
      duration: 1300,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      once: false,
      mirror: true,
      offset: 140,
      delay: 120
    });
  }, []);

  return (
    <div className="font-body bg-slate-50 text-slate-700">
      <header
        className="relative min-h-screen overflow-hidden"
        style={{
          backgroundImage:
            'linear-gradient(to bottom right, rgba(15, 23, 42, 0.35), rgba(59, 130, 246, 0.2)), url(https://images.unsplash.com/photo-1518391846015-55a9cc003b25?auto=format&fit=crop&w=2000&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="mx-auto flex min-h-screen max-w-6xl items-center px-6 py-20 md:px-10">
          <div
            className="max-w-3xl"
            data-aos="fade-up"
            data-aos-duration="1600"
            data-aos-anchor-placement="top-bottom"
          >
            <p className="mb-4 text-sm uppercase tracking-[0.2em] text-sky-100/90">
              Portfolio
            </p>
            <h1 className="font-heading text-5xl leading-tight text-white sm:text-6xl md:text-7xl">
              Rishi Mitra
            </h1>
            <p className="mt-6 max-w-2xl text-xl leading-relaxed text-slate-100 sm:text-2xl">
              Building elegant digital products with thoughtful design, strong engineering, and smooth user experiences.
            </p>
            <a
              href="#projects"
              className="mt-10 inline-flex rounded-full bg-sky-500 px-7 py-3 text-base font-semibold text-white transition duration-300 hover:-translate-y-0.5 hover:bg-sky-600"
            >
              View Projects
            </a>
          </div>
        </div>
      </header>

      <main>
        <section id="about" className="mx-auto max-w-6xl px-6 py-20 md:px-10">
          <div
            className="rounded-3xl border border-slate-200 bg-white p-8 shadow-lg shadow-slate-200/70 md:p-12"
            data-aos="fade-up"
            data-aos-duration="1500"
            data-aos-anchor-placement="top-bottom"
          >
            <h2 className="font-heading text-4xl text-slate-800">About</h2>
            <p className="mt-5 text-xl leading-relaxed text-slate-600">
              I am a creative developer passionate about building meaningful digital experiences that blend aesthetics
              with functionality. This section can be replaced with your professional story, technical strengths, and
              career highlights.
            </p>
          </div>
        </section>

        <section id="projects" className="mx-auto max-w-6xl px-6 py-4 md:px-10 md:py-8">
          <div
            className="mb-10"
            data-aos="fade-up"
            data-aos-duration="1500"
            data-aos-anchor-placement="top-bottom"
          >
            <h2 className="font-heading text-4xl text-slate-800">Projects</h2>
            <p className="mt-3 text-lg text-slate-500">
              A selection of sample work showcasing product thinking and technical depth.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {projects.map((project, index) => (
              <article
                key={project.title}
                className="rounded-2xl border border-slate-200 bg-white p-7 shadow-md shadow-slate-200/60 transition-all duration-300 hover:-translate-y-1 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100"
                data-aos="fade-up"
                data-aos-delay={index * 180}
                data-aos-duration="1400"
                data-aos-anchor-placement="top-bottom"
              >
                <h3 className="font-heading text-2xl text-slate-800">{project.title}</h3>
                <p className="mt-3 text-lg leading-relaxed text-slate-600">{project.description}</p>
                <p className="mt-5 text-sm uppercase tracking-widest text-sky-600">{project.stack}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="contact" className="mx-auto max-w-6xl px-6 py-20 md:px-10">
          <div
            className="rounded-3xl border border-slate-200 bg-gradient-to-r from-slate-100 via-white to-sky-50 p-8 shadow-lg shadow-slate-200/70 md:p-12"
            data-aos="fade-up"
            data-aos-duration="1500"
            data-aos-anchor-placement="top-bottom"
          >
            <h2 className="font-heading text-4xl text-slate-800">Contact</h2>
            <p className="mt-4 max-w-3xl text-xl leading-relaxed text-slate-600">
              Let&apos;s connect and collaborate on meaningful projects. Reach out through the platforms below.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              {socialLinks.map((link, index) => (
                <a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider text-slate-700 transition duration-300 hover:border-sky-500 hover:text-sky-600"
                  data-aos="fade-up"
                  data-aos-delay={250 + index * 180}
                  data-aos-duration="1300"
                  data-aos-anchor-placement="top-bottom"
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default App;
